import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Home, User, Briefcase, Mail, Loader2, Sparkles, LayoutTemplate, FileText } from 'lucide-react';
import { useStaticContent, useUpdateStaticContent } from '@/hooks/use-site-settings';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminContent() {
  const { data: homeContent } = useStaticContent('home');
  const { data: aboutContent } = useStaticContent('about');
  const { data: servicesContent } = useStaticContent('services');
  const { data: contactContent } = useStaticContent('contact');

  const updateContent = useUpdateStaticContent();

  const [activeTab, setActiveTab] = useState('home');
  const [saving, setSaving] = useState(false);

  const [pageContent, setPageContent] = useState<Record<string, any>>({});

  // Initialize state when data is loaded
  useEffect(() => {
    const newContent: Record<string, any> = {};
    if (homeContent) {
      newContent.home = {};
      homeContent.forEach(item => {
        try {
          newContent.home[item.section] = JSON.parse(item.content);
        } catch (e) {
          newContent.home[item.section] = {};
        }
      });
    }
    if (aboutContent) {
      newContent.about = {};
      aboutContent.forEach(item => {
        try {
          newContent.about[item.section] = JSON.parse(item.content);
        } catch (e) {
          newContent.about[item.section] = {};
        }
      });
    }
    if (servicesContent) {
      newContent.services = {};
      servicesContent.forEach(item => {
        try {
          newContent.services[item.section] = JSON.parse(item.content);
        } catch (e) {
          newContent.services[item.section] = {};
        }
      });
    }
    if (contactContent) {
      newContent.contact = {};
      contactContent.forEach(item => {
        try {
          newContent.contact[item.section] = JSON.parse(item.content);
        } catch (e) {
          newContent.contact[item.section] = {};
        }
      });
    }
    setPageContent(prev => ({ ...prev, ...newContent }));
  }, [homeContent, aboutContent, servicesContent, contactContent]);


  // Helper to safely get value, preferring local state over fetched data
  const getContentValue = (page: string, section: string, field: string) => {
    if (pageContent[page]?.[section]?.[field] !== undefined) {
      return pageContent[page][section][field];
    }
    return '';
  };

  const handleContentChange = (page: string, section: string, field: string, value: any) => {
    setPageContent(prev => ({
      ...prev,
      [page]: {
        ...(prev[page] || {}),
        [section]: {
          ...(prev[page]?.[section] || {}),
          [field]: value
        }
      }
    }));
  };

  const savePageContent = async (page: string, section: string) => {
    try {
      setSaving(true);

      // Get current data to ensure we have the latest base
      let currentData = [];
      if (page === 'home') currentData = homeContent || [];
      else if (page === 'about') currentData = aboutContent || [];
      else if (page === 'services') currentData = servicesContent || [];
      else if (page === 'contact') currentData = contactContent || [];

      const currentSection = currentData.find(s => s.section === section);
      let baseContent = {};
      if (currentSection) {
        try {
          baseContent = JSON.parse(currentSection.content);
        } catch { }
      }

      const localChanges = pageContent[page]?.[section] || {};
      const finalContent = { ...baseContent, ...localChanges };

      await updateContent.mutateAsync({ page, section, content: JSON.stringify(finalContent) });
      toast.success('Conteúdo salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
      toast.error('Erro ao salvar conteúdo');
    } finally {
      setSaving(false);
    }
  };

  const renderContentField = (label: string, field: string, section: string, type: 'text' | 'textarea' = 'text', rows?: number) => {
    const value = getContentValue(activeTab, section, field);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2 group"
      >
        <Label htmlFor={field} className="text-zinc-400 group-focus-within:text-blue-400 transition-colors pointer-events-none">
          {label}
        </Label>
        {type === 'textarea' ? (
          <Textarea
            id={field}
            value={value}
            onChange={(e) => handleContentChange(activeTab, section, field, e.target.value)}
            rows={rows || 4}
            placeholder={`Digite ${label.toLowerCase()}...`}
            className="bg-zinc-900/50 border-white/10 focus:border-blue-500/50 focus:ring-blue-500/20 text-zinc-100 placeholder:text-zinc-600 resize-none transition-all duration-300"
          />
        ) : (
          <Input
            id={field}
            value={value}
            onChange={(e) => handleContentChange(activeTab, section, field, e.target.value)}
            placeholder={`Digite ${label.toLowerCase()}...`}
            className="bg-zinc-900/50 border-white/10 focus:border-blue-500/50 focus:ring-blue-500/20 text-zinc-100 placeholder:text-zinc-600 h-11 transition-all duration-300"
          />
        )}
      </motion.div>
    );
  };

  const tabs = [
    { id: 'home', label: 'Home', icon: Home, desc: 'Página Inicial' },
    { id: 'about', label: 'Sobre', icon: User, desc: 'Quem Sou' },
    { id: 'services', label: 'Serviços', icon: Briefcase, desc: 'O que Faço' },
    { id: 'contact', label: 'Contato', icon: Mail, desc: 'Fale Comigo' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Conteúdo do Site</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie os textos estáticos e informações principais de cada página
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl" />
          <TabsList className="relative w-full grid grid-cols-4 h-auto p-1 bg-zinc-950/80 backdrop-blur-xl border border-white/10 rounded-2xl gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className={`
                            relative h-20 flex flex-col items-center justify-center gap-2 rounded-xl transition-all duration-300
                            ${isActive ? 'bg-white/10 text-white shadow-lg' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}
                        `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-blue-400' : 'text-zinc-500'}`} />
                  <div className="text-center">
                    <span className="block font-medium">{tab.label}</span>
                    <span className="text-[10px] opacity-60 hidden md:block">{tab.desc}</span>
                  </div>
                  {isActive && (
                    <motion.div
                      layoutId="activeTabGlow"
                      className="absolute inset-0 bg-blue-500/10 rounded-xl"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* Home Page */}
            <TabsContent value="home" className="space-y-8 mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Hero Section */}
                <div className="lg:col-span-2 space-y-8">
                  <div className="bg-zinc-950/50 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden">
                    <div className="p-8 border-b border-white/5 flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400">
                        <LayoutTemplate className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Seção Hero</h3>
                        <p className="text-sm text-zinc-400">Conteúdo principal da página inicial</p>
                      </div>
                    </div>
                    <div className="p-8 space-y-6">
                      {renderContentField('Título Principal', 'title', 'hero')}
                      {renderContentField('Subtítulo', 'subtitle', 'hero', 'textarea', 3)}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {renderContentField('Texto do Botão', 'cta_text', 'hero')}
                        {renderContentField('Link do Botão', 'cta_link', 'hero')}
                      </div>
                    </div>
                    <div className="p-6 bg-white/2 border-t border-white/5 flex justify-end">
                      <Button
                        onClick={() => savePageContent('home', 'hero')}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-500 text-white min-w-[140px]"
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Salvar Hero
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sidebar / Info */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-[2rem] border border-white/10 p-8">
                    <div className="flex items-center gap-3 mb-4 text-indigo-400">
                      <Sparkles className="w-5 h-5" />
                      <h3 className="font-semibold">Dica Pro</h3>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                      A seção Hero é a primeira coisa que seus visitantes veem. Use um título impactante e sucinto para capturar a atenção imediatamente.
                    </p>
                    <div className="space-y-3">
                      <div className="text-xs font-mono text-zinc-500 bg-black/20 p-3 rounded-lg border border-white/5">
                        Recomendado: 40-60 caracteres
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* About Page */}
            <TabsContent value="about" className="space-y-8 mt-0">
              <div className="bg-zinc-950/50 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden">
                <div className="p-8 border-b border-white/5 flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-purple-500/10 text-purple-400">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Sobre Mim (Hero)</h3>
                    <p className="text-sm text-zinc-400">Apresentação pessoal</p>
                  </div>
                </div>
                <div className="p-8 space-y-6">
                  {renderContentField('Título', 'title', 'hero')}
                  {renderContentField('Subtítulo', 'subtitle', 'hero', 'textarea', 2)}
                  {renderContentField('Descrição Completa', 'description', 'hero', 'textarea', 6)}
                </div>
                <div className="p-6 bg-white/2 border-t border-white/5 flex justify-end">
                  <Button
                    onClick={() => savePageContent('about', 'hero')}
                    disabled={saving}
                    className="bg-purple-600 hover:bg-purple-500 text-white min-w-[140px]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar
                  </Button>
                </div>
              </div>

              <div className="bg-zinc-950/50 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden">
                <div className="p-8 border-b border-white/5 flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-amber-500/10 text-amber-400">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Skills e Expertise</h3>
                    <p className="text-sm text-zinc-400">Lista de habilidades técnicas</p>
                  </div>
                </div>
                <div className="p-8 space-y-6">
                  {renderContentField('Título da Seção', 'title', 'skills')}
                  {renderContentField('Subtítulo', 'subtitle', 'skills', 'textarea', 2)}
                  <div className="space-y-2">
                    {renderContentField('JSON de Skills', 'skills', 'skills', 'textarea', 8)}
                    <p className="text-xs text-zinc-500 font-mono pl-1">Ex: {'[{"name": "React", "level": 90}]'}</p>
                  </div>
                </div>
                <div className="p-6 bg-white/2 border-t border-white/5 flex justify-end">
                  <Button
                    onClick={() => savePageContent('about', 'skills')}
                    disabled={saving}
                    className="bg-amber-600 hover:bg-amber-500 text-white min-w-[140px]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar Skills
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Services Page */}
            <TabsContent value="services" className="space-y-8 mt-0">
              <div className="bg-zinc-950/50 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden">
                <div className="p-8 border-b border-white/5 flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Cabeçalho de Serviços</h3>
                    <p className="text-sm text-zinc-400">Título e introdução da página</p>
                  </div>
                </div>
                <div className="p-8 space-y-6">
                  {renderContentField('Título', 'title', 'hero')}
                  {renderContentField('Subtítulo', 'subtitle', 'hero', 'textarea', 2)}
                </div>
                <div className="p-6 bg-white/2 border-t border-white/5 flex justify-end">
                  <Button
                    onClick={() => savePageContent('services', 'hero')}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white min-w-[140px]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar
                  </Button>
                </div>
              </div>

              <div className="p-8 rounded-[2rem] border border-dashed border-white/10 flex flex-col items-center justify-center text-center text-zinc-500">
                <Briefcase className="w-8 h-8 mb-4 opacity-50" />
                <p>Os serviços individuais são gerenciados na aba lateral "Serviços".</p>
              </div>
            </TabsContent>

            {/* Contact Page */}
            <TabsContent value="contact" className="space-y-8 mt-0">
              <div className="bg-zinc-950/50 backdrop-blur-md rounded-[2rem] border border-white/5 overflow-hidden">
                <div className="p-8 border-b border-white/5 flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-pink-500/10 text-pink-400">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Página de Contato</h3>
                    <p className="text-sm text-zinc-400">Mensagem de convite</p>
                  </div>
                </div>
                <div className="p-8 space-y-6">
                  {renderContentField('Título', 'title', 'hero')}
                  {renderContentField('Subtítulo', 'subtitle', 'hero', 'textarea', 2)}
                  {renderContentField('Texto de Apoio', 'description', 'hero', 'textarea', 4)}
                </div>
                <div className="p-6 bg-white/2 border-t border-white/5 flex justify-end">
                  <Button
                    onClick={() => savePageContent('contact', 'hero')}
                    disabled={saving}
                    className="bg-pink-600 hover:bg-pink-500 text-white min-w-[140px]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar
                  </Button>
                </div>
              </div>
            </TabsContent>
          </motion.div>
        </AnimatePresence>
      </Tabs>
    </motion.div>
  );
}
